import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Clock, User, Share2, MessageCircle, Facebook, Twitter, Copy, QrCode } from "lucide-react";
import AdSlot from "@/components/AdSlot";
import InArticleAd from "@/components/InArticleAd";
import { GalleryViewer } from "@/components/Gallery";
import CommentsSection from "@/components/CommentsSection";
import EditorSignatureBox from "@/components/EditorSignatureBox";
import { ReadAloudButton, ShareButtons, BookmarkButton, RelatedArticles, PollWidget } from "@/components/ArticleEnhancements";
import EPaperDownloadWidget from "@/components/EPaperDownloadWidget";

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    setArticle(null);
    api.get(`/news/slug/${slug}`).then((r) => {
      setArticle(r.data);
      api.post(`/news/${slug}/view`).catch(() => {});
      api.get("/news", { params: { category: r.data.category, limit: 4 } })
        .then((rr) => setRelated(rr.data.filter((n) => n.slug !== r.data.slug).slice(0, 3)));
    }).catch((err) => {
      if (err?.response?.status === 404) setNotFound(true);
    });
  }, [slug]);

  // Inject JSON-LD schema for SEO/Google News
  useEffect(() => {
    if (!article) return;
    const jsonld = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: article.title,
      image: article.image_url ? [article.image_url] : [],
      datePublished: article.created_at,
      dateModified: article.updated_at || article.created_at,
      author: { "@type": "Person", name: article.author || "JAKIR MOMIN" },
      publisher: {
        "@type": "Organization", name: "INDIAN-NEWS-21",
        logo: { "@type": "ImageObject", url: `${window.location.origin}/logo.png` }
      },
      description: article.excerpt,
      articleSection: article.category,
      mainEntityOfPage: window.location.href,
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(jsonld);
    document.head.appendChild(script);
    // Update page title + meta description for SEO
    const prevTitle = document.title;
    document.title = `${article.title} — INDIAN-NEWS-21`;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", article.excerpt || article.title);
    return () => {
      script.remove();
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, [article]);

  if (!article) return (<><PublicHeader />{notFound ? (
    <div className="max-w-3xl mx-auto p-10 text-center" data-testid="article-not-found">
      <div className="text-6xl mb-4">📰</div>
      <h1 className="font-devnagri-heading text-3xl font-black text-[#0F172A] mb-2">यह समाचार नहीं मिला</h1>
      <p className="text-slate-600 mb-6">शायद यह हटा दिया गया या URL ग़लत है।</p>
      <Link to="/" className="inline-flex items-center gap-2 bg-[#0F172A] hover:bg-[#1E3A8A] text-white px-5 py-2.5 rounded-md text-sm font-bold">मुखपृष्ठ पर जाएँ</Link>
    </div>
  ) : (
    <div className="max-w-3xl mx-auto p-10 text-slate-500">लोड हो रहा है…</div>
  )}<PublicFooter /></>);

  const shareUrl = window.location.href;
  const shareText = `${article.title} — समाचार`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const twUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("लिंक कॉपी हो गया"); }
    catch { toast.error("कॉपी नहीं हो सका"); }
  };

  return (
    <>
      <PublicHeader />
      <article className="max-w-3xl mx-auto px-4 py-5 md:py-10" data-testid="article-page">
        {/* Brand watermark strip — shown on every news post (compact on mobile) */}
        <div className="mb-4 md:mb-6 pb-3 md:pb-4 border-b border-slate-200 flex items-center gap-2 md:gap-3" data-testid="brand-strip">
          <img src="/logo.png" alt="INDIAN-NEWS-21" className="w-10 h-10 md:w-14 md:h-14 rounded object-contain bg-white shadow-sm"/>
          <div className="flex-1 min-w-0">
            <div className="font-devnagri-heading font-black text-sm md:text-lg text-[#0F172A] leading-none tracking-tight truncate">INDIAN-NEWS-21</div>
            <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-amber-600 font-bold mt-0.5">भारत की आवाज़</div>
          </div>
          <div className="bg-gradient-to-br from-amber-400 to-amber-600 text-[#0F172A] rounded-lg px-2 md:px-3 py-1 md:py-1.5 shadow-md shrink-0">
            <div className="text-[7px] md:text-[8px] uppercase tracking-widest font-black opacity-80">Chief Editor</div>
            <div className="font-black text-[11px] md:text-sm tracking-wider leading-none">JAKIR MOMIN</div>
          </div>
        </div>
        <div className="mb-3 md:mb-4 flex items-center gap-2 md:gap-3 flex-wrap">
          <Link to={`/category/${article.category}`} className="tag-cat">{article.category}</Link>
          {article.is_breaking && <span className="tag-breaking">ब्रेकिंग</span>}
          {article.is_editorial && (
            <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#EA580C] to-[#DC2626] text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded" data-testid="editorial-badge">
              ✦ संपादकीय
            </span>
          )}
        </div>
        <h1 className="font-devnagri-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black leading-tight text-[#0F172A] mb-3 md:mb-5">
          {article.title}
        </h1>
        <p className="text-base md:text-xl text-slate-600 leading-relaxed mb-4 md:mb-6">{article.excerpt}</p>
        <div className="flex items-center gap-5 text-sm text-slate-500 font-admin mb-4 pb-6 border-b border-slate-200 flex-wrap">
          <span className="flex items-center gap-1.5"><User className="w-4 h-4"/>{article.author}</span>
          <span className="flex items-center gap-1.5"><Clock className="w-4 h-4"/>{new Date(article.created_at).toLocaleString("hi-IN")}</span>
          <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200 rounded-full px-2.5 py-0.5 font-bold text-xs" data-testid="views-pill">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/></svg>
            {(article.views || 0).toLocaleString("hi-IN")} व्यू
          </span>
        </div>
        {article.reporter_badge && (
          <Link to="/reporters" className="mb-4 inline-flex items-center gap-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-full pl-1 pr-4 py-1 hover:shadow-md transition" data-testid="reporter-badge">
            {article.reporter_badge.photo_url ? (
              <img src={article.reporter_badge.photo_url} alt="" className="w-8 h-8 rounded-full object-cover"/>
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center font-black text-xs">{article.reporter_badge.name[0]}</div>
            )}
            <div className="text-xs">
              <div><b className="text-[#0F172A]">{article.reporter_badge.name}</b> <span className="text-slate-500">ने रिपोर्ट किया</span></div>
              <div className="text-[10px] text-amber-700 font-bold uppercase tracking-widest">Rank #{article.reporter_badge.rank} · {"★".repeat(article.reporter_badge.stars)}{"☆".repeat(5 - article.reporter_badge.stars)} · इस महीने</div>
            </div>
          </Link>
        )}
        <div className="mb-6">
          <a href={`${API}/news/slug/${slug}/poster`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-full px-3 py-1 transition"
            title="A4 QR पोस्टर प्रिंट करें — दुकानदारों के लिए"
            data-testid="qr-poster-btn">
            <QrCode className="w-3.5 h-3.5"/> QR पोस्टर प्रिंट करें
          </a>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-8">
          <ReadAloudButton text={`${article.title}. ${article.excerpt}. ${article.content}`}/>
          <BookmarkButton slug={article.slug} title={article.title}/>
          <ShareButtons url={typeof window !== "undefined" ? window.location.href : ""} title={article.title}/>
          <EPaperDownloadWidget slug={article.slug} title={article.title}/>
        </div>
        {article.video_url && (
          <>
            <div className="mb-3 aspect-video rounded-lg overflow-hidden bg-black" data-testid="youtube-embed">
              {(() => {
                const v = article.video_url;
                const m = v.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
                const vid = m ? m[1] : null;
                if (vid) return <iframe title="video" width="100%" height="100%" src={`https://www.youtube.com/embed/${vid}`} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>;
                return <video src={v} controls className="w-full h-full"/>;
              })()}
            </div>
            {/* Self-hosted MP4: show download + WhatsApp forward CTAs */}
            {!/(youtu\.be|youtube\.com)/.test(article.video_url) && (
              <div className="mb-8 flex flex-wrap items-center gap-2" data-testid="video-actions">
                <a href={article.video_url} download
                  className="inline-flex items-center gap-1.5 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-bold px-3 py-2 rounded-md transition"
                  data-testid="video-download-btn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3v10.586l3.293-3.293 1.414 1.414L12 16.414l-4.707-4.707 1.414-1.414L12 13.586V3h0zm-8 16v2h16v-2H4z"/></svg>
                  MP4 डाउनलोड करें
                </a>
                <a href={`https://wa.me/?text=${encodeURIComponent(`📢 INDIAN-NEWS-21 · ${article.title}\n\n${(article.excerpt || "").slice(0, 200)}\n\n▶️ Video देखें: ${typeof window !== "undefined" ? window.location.href : ""}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded-md transition"
                  data-testid="video-wa-btn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.76.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22c5.46 0 9.91-4.45 9.91-9.93S17.5 2 12.04 2zm5.72 14.16c-.24.67-1.4 1.28-1.94 1.32-.52.04-1.02.22-3.44-.75-2.87-1.14-4.75-4.04-4.9-4.24-.15-.2-1.18-1.58-1.18-3.02s.76-2.14 1.03-2.44c.27-.3.6-.37.8-.37h.57c.18 0 .43-.07.68.52.24.6.83 2.06.9 2.21.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.17-.32.38-.45.51-.15.15-.3.32-.13.62.17.3.75 1.24 1.62 2 1.12 1 2.07 1.31 2.37 1.46.3.15.47.13.65-.07.17-.2.75-.87.95-1.17.2-.3.4-.25.67-.15.27.1 1.72.81 2.02.96.3.15.5.22.57.35.07.13.07.72-.17 1.4z"/></svg>
                  WhatsApp पर भेजें
                </a>
                <span className="text-[10px] text-slate-500 ml-auto">📱 Save karke friends ko forward kar do</span>
              </div>
            )}
          </>
        )}
        {!article.video_url && article.image_url && (
          <img src={article.image_url} alt={article.title} className="w-full rounded-lg mb-8 aspect-video object-cover" />
        )}

        <AdSlot placement="article_top" className="mb-8"/>

        <div className="article-body font-serif-hi text-[#0F172A]">
          {(article.content || "").split("\n\n").map((p, i, arr) => (
            <span key={`pw-${article.id}-${i}`}>
              <p key={`p-${article.id}-${i}`}>{p}</p>
              {/* Inject an AdSense in-article ad every 3 paragraphs (skip last) */}
              {(i + 1) % 3 === 0 && i < arr.length - 1 && <InArticleAd/>}
            </span>
          ))}
        </div>

        <GalleryViewer images={article.gallery_images}/>

        {/* Share bar */}
        <div className="mt-8 pt-6 border-t border-slate-200">
          <div className="text-xs font-admin uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2"><Share2 className="w-3.5 h-3.5"/>यह समाचार साझा करें</div>
          <div className="flex flex-wrap gap-2">
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-md text-sm font-medium" data-testid="share-whatsapp">
              <MessageCircle className="w-4 h-4"/> WhatsApp
            </a>
            <a href={fbUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#1877F2] hover:bg-[#0f65db] text-white px-4 py-2 rounded-md text-sm font-medium" data-testid="share-facebook">
              <Facebook className="w-4 h-4"/> Facebook
            </a>
            <a href={twUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-black hover:bg-slate-800 text-white px-4 py-2 rounded-md text-sm font-medium" data-testid="share-twitter">
              <Twitter className="w-4 h-4"/> Twitter
            </a>
            <button onClick={copyLink} className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-800 px-4 py-2 rounded-md text-sm font-medium" data-testid="share-copy">
              <Copy className="w-4 h-4"/> लिंक कॉपी
            </button>
          </div>
        </div>
        {article.tags?.length > 0 && (
          <div className="mt-8 pt-6 border-t border-slate-200 flex flex-wrap gap-2">
            {article.tags.map((t) => <span key={t} className="text-xs bg-slate-100 text-slate-700 px-3 py-1 rounded-full font-admin">#{t}</span>)}
          </div>
        )}

        {related.length > 0 && (
          <div className="mt-12">
            <h3 className="font-devnagri-heading text-xl font-bold mb-4 border-l-4 border-[#DC2626] pl-3">संबंधित समाचार</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.id} to={`/article/${r.slug}`} className="group">
                  <div className="img-hover-zoom rounded-md aspect-video bg-slate-200 mb-2">
                    {r.image_url && <img src={r.image_url} alt={r.title} className="w-full h-full object-cover"/>}
                  </div>
                  <h4 className="font-devnagri-heading font-bold text-sm news-title line-clamp-3">{r.title}</h4>
                </Link>
              ))}
            </div>
          </div>
        )}

        <AdSlot placement="article_bottom" className="mt-10"/>

        <PollWidget newsId={article.id}/>
        <RelatedArticles slug={article.slug}/>

        <EditorSignatureBox />

        <CommentsSection slug={article.slug}/>
      </article>
      <PublicFooter />
    </>
  );
}
